import {
  Component,
  Injector,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
  ViewEncapsulation,
} from "@angular/core";
import { LineChartComponent } from "../../components/line-chart/line-chart.component";
import moment from "moment";
import * as Highcharts from "highcharts/highstock";
import {
  HC_LEGEND_STYLE,
  HC_CROSSHAIR_STYLE,
  HC_COLORS,
  HC_RANGE_SELECTOR,
} from "../highcharts-shared";
import { ChartPlaceholderComponent } from "src/app/components/chart-placeholder/chart-placeholder.component";
import {
  HighchartsChartComponent,
  providePartialHighcharts,
} from "highcharts-angular";

@Component({
  selector: "app-cummulative-return-chart",
  templateUrl: "./cummulative-return-chart.component.html",
  styleUrls: ["./cummulative-return-chart.component.scss"],
  encapsulation: ViewEncapsulation.None,
  imports: [ChartPlaceholderComponent, HighchartsChartComponent],
  providers: [
    providePartialHighcharts({
      modules: () => [
        import("highcharts/esm/modules/stock"),
        import("highcharts/esm/modules/series-label"),
      ],
    }),
  ],
  standalone: true,
})
export class CummulativeReturnChartComponent
  extends LineChartComponent
  implements OnInit, OnChanges
{
  isHighcharts = typeof Highcharts === "object";
  @Input() plotData: any = {};
  @Input() subHeader: string = "";
  @Input() highlightStart: any = null;
  @Input() highlightEnd: any = null;

  workingPlotData: any = {};
  portfolioStart: any = null;
  private dataStart: number = null;
  private dataEnd: number = null;
  private originalSeriesData: number[][][] = [];
  private isRebasing = false;

  series = [];

  constructor(injector: Injector) {
    super(injector);
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.startDate || changes.endDate) {
      this.updateDates();
    }
    if (changes.plotData || changes.benchmarks || changes.benchmark) {
      this.internalReady.set(false);
      this.initPlot();
    }
  }

  ngOnInit(): void {
    this.initPlot();
  }

  initPlot() {
    if (!this.plotData) {
      return false;
    }
    this.workingPlotData = JSON.parse(JSON.stringify(this.plotData));
    this.series = this.workingPlotData.cumulative_returns
      ? this.extractPlotData(
          this.workingPlotData.cumulative_returns,
          null,
          true,
        )
      : [];

    if (this.series.length) {
      const portfolio = this.series[0];
      if (portfolio.data && portfolio.data.length) {
        this.dataStart = portfolio.data[0][0];
        this.dataEnd = portfolio.data[portfolio.data.length - 1][0];
      }
    }
    let benchCumRet: any = null;
    if (this.benchmark) {
      benchCumRet = this.extractBenchmarkData(
        this.benchmark.cumulative_returns,
        this.benchmark.index_name_short,
        this.dataStart,
        this.dataEnd,
      );
    }

    if (benchCumRet) {
      this.series.push(benchCumRet);
    }
    this.series[0].data.map((item) => (item[1] -= 100));
    this.series[0].type = "area";
    this.series[0].color = HC_COLORS.accent;
    this.series[0].lineWidth = 2;
    this.series[0].zIndex = 2;
    this.series[0].marker = { enabled: false };
    // Soft purple-to-transparent gradient — matches the home-experience prototype.
    this.series[0].fillColor = {
      linearGradient: { x1: 0, x2: 0, y1: 0, y2: 1 },
      stops: [
        [0, "rgba(124,92,255,0.45)"],
        [1, "rgba(124,92,255,0.02)"],
      ],
    };
    if (this.series[1]) {
      this.series[1].color = HC_COLORS.info;
      this.series[1].lineWidth = 2;
      this.series[1].zIndex = 1;
      this.series[1].marker = { enabled: false };
    }
    this.originalSeriesData = this.series.map((s) => s.data.map((d) => [...d]));

    // Pre-rebase to the full window so the chart renders correctly on first
    // paint. afterSetExtremes fires during chart init before chartInstance is
    // available, so rebaseToWindow's first invocation is a no-op — without
    // this, the portfolio renders in raw (value − 100) form and starts off
    // the 0% baseline whenever the source series doesn't begin at 100.
    for (let i = 0; i < this.series.length; i++) {
      this.series[i].data = this.computeRebasedData(
        this.originalSeriesData[i],
        this.dataStart,
      );
    }

    this.initChart();
  }

  initChart() {
    let highlight = null;

    if (this.highlightStart && this.highlightEnd) {
      highlight = {
        color: HC_COLORS.accentSoft,
        from: this.highlightStart,
        to: this.highlightEnd,
      };
    }

    const buttons = this.getAvailableRangeButtons();

    this.chartOptions = {
      chart: {
        height: this.isPrint ? 460 : 510,
        backgroundColor: "transparent",
        spacing: [12, 4, 8, 4],
        style: { fontFamily: "Inter, system-ui, sans-serif" },
        zooming: { mouseWheel: { enabled: false } },
        resetZoomButton: { position: { x: 0, y: -28 } },
      },
      credits: { enabled: false },
      title: { text: "" },
      navigator: { enabled: false, maskInside: false },
      scrollbar: { enabled: false },
      rangeSelector: {
        ...HC_RANGE_SELECTOR,
        buttons,
        selected: buttons.length - 1,
      },
      xAxis: {
        type: "datetime",
        lineColor: "transparent",
        tickColor: "transparent",
        plotBands: highlight ? [highlight] : undefined,
        crosshair: {
          ...HC_CROSSHAIR_STYLE,
          label: { enabled: false },
        },
        labels: { style: { color: "var(--text-dim)" } },
        events: {
          afterSetExtremes: (e) => this.rebaseToWindow(e.min, e.max),
        },
      },
      yAxis: [
        {
          opposite: false,
          gridLineColor: "var(--border)",
          gridLineDashStyle: "ShortDash",
          gridLineWidth: 1,
          minorGridLineWidth: 0,
          title: { text: "" },
          labels: { format: "{text}%", style: { color: "var(--text-dim)" } },
        },
      ],
      legend: {
        // A single series makes the legend pointless — there is nothing to
        // toggle. Only show it once a benchmark (or any extra series) is added.
        enabled: this.series.length > 1,
        align: "left",
        verticalAlign: "bottom",
        layout: "horizontal",
        itemDistance: 18,
        margin: 12,
        padding: 0,
        symbolRadius: 2,
        symbolHeight: 3,
        symbolWidth: 14,
        squareSymbol: false,
        ...HC_LEGEND_STYLE,
      },
      tooltip: {
        // Stock charts force `outside: true` and `split: true` on top of the
        // global defaults; override both so the tooltip uses the shared
        // single-box styling.
        outside: false,
        split: false,
        shared: true,
        useHTML: true,
        formatter(this: any) {
          const points = this.points as any[];
          const date = moment(this.x).format("YYYY-MM-DD");
          const rows = points
            .map(
              (p) =>
                `<div style="display:flex;align-items:center;gap:10px;margin-top:4px;">
                   <span style="display:inline-block;width:8px;height:8px;border-radius:50%;background:${p.color};flex:none;"></span>
                   <span style="color:var(--text-dim);">${p.series.name}</span>
                   <span style="margin-left:auto;font-family:var(--font-mono);font-variant-numeric:tabular-nums;font-weight:600;color:var(--text);">${p.y.toFixed(2)}%</span>
                 </div>`,
            )
            .join("");
          return `<div style="min-width:200px;font-size:12px;color:var(--text);"><div style="font-size:10.5px;letter-spacing:0.06em;color:var(--text-dim);text-transform:uppercase;margin-bottom:4px;font-weight:500;">${date}</div>${rows}</div>`;
        },
      },
      plotOptions: {
        series: {
          lineWidth: 2,
          dataGrouping: { enabled: false },
          marker: {
            enabled: false,
            states: { hover: { enabled: true, radius: 4 } },
          },
          states: { hover: { lineWidthPlus: 0 } },
          label: { enabled: false },
        },
        area: {
          fillOpacity: 1,
          lineWidth: 2,
        },
      },
      series: this.series,
    };
    if (this.fullHeight) {
      this.chartOptions.chart.height = "96%";
    }
    if (this.extraOptions) {
      this.chartOptions = {
        ...this.chartOptions,
        ...this.extraOptions,
      };
    }
    this.internalReady.set(true);
    this.cdr.markForCheck();
  }

  extractPlotData(list, start?, saveStart = false) {
    if (saveStart) {
      this.portfolioStart = list["Portfolio"][0][0]; // First item, first column is the start date
    }
    return this.util.extractPlotData(list, start);
  }

  extractBenchmarkData(list, name, start?, end?) {
    // convert string dates to timestamps and value tuples
    let convertedList = list.map((data: any) => {
      return [data.date, data.value];
    });
    if (start) {
      // Skip benchmark values that older then portfolio start
      convertedList = convertedList.filter((d) => d[0] >= start && d[0] <= end);

      // order convertedList by date
      convertedList = convertedList.sort((a, b) => a[0] - b[0]);

      const initalPortfolioValue = this.util.getClosestDate(
        convertedList,
        this.series[0].data,
      ); // Get the value at the cloest date
      if (initalPortfolioValue) {
        const benchValue = convertedList[0][1];
        convertedList = convertedList.map((d) => {
          d[1] = (d[1] / benchValue - 1) * 100;
          return d;
        });
      }
    }

    return {
      name: name ?? "Benchmark",
      data: convertedList,
      type: "line",
    };
  }

  private computeRebasedData(
    originalData: number[][],
    min: number,
  ): number[][] {
    const firstVisiblePoint = originalData.find((d) => d[0] >= min);
    const baseValue = firstVisiblePoint ? 100 + firstVisiblePoint[1] : 100;
    return originalData.map((d) => [
      d[0],
      +(((100 + d[1]) / baseValue - 1) * 100).toFixed(2),
    ]);
  }

  private rebaseToWindow(min: number, max: number) {
    if (
      this.isRebasing ||
      !this.chartInstance ||
      !this.originalSeriesData.length
    )
      return;
    this.isRebasing = true;

    try {
      for (let i = 0; i < this.originalSeriesData.length; i++) {
        const rebasedData = this.computeRebasedData(
          this.originalSeriesData[i],
          min,
        );
        if (this.chartInstance.series[i]) {
          this.chartInstance.series[i].setData(
            rebasedData,
            false,
            false,
            false,
          );
        }
      }
      this.chartInstance.redraw(false);
    } finally {
      this.isRebasing = false;
    }
  }

  private getAvailableRangeButtons(): any[] {
    const allButtons = [
      { type: "month", count: 1, text: "1M", title: "View 1 month" },
      { type: "month", count: 3, text: "3M", title: "View 3 months" },
      { type: "month", count: 6, text: "6M", title: "View 6 months" },
      { type: "ytd", text: "YTD", title: "View year to date" },
      { type: "year", count: 1, text: "1Y", title: "View 1 year" },
      { type: "year", count: 3, text: "3Y", title: "View 3 years" },
      { type: "year", count: 5, text: "5Y", title: "View 5 years" },
      { type: "year", count: 10, text: "10Y", title: "View 10 years" },
      { type: "all", text: "ALL", title: "View all" },
    ];

    if (!this.dataStart || !this.dataEnd) {
      return allButtons;
    }

    const dataSpanMs = this.dataEnd - this.dataStart;
    const oneMonthMs = 30.44 * 24 * 60 * 60 * 1000;
    const oneYearMs = 365.25 * 24 * 60 * 60 * 1000;

    return allButtons.filter((button) => {
      if (button.type === "all" || button.type === "ytd") return true;
      if (button.type === "month")
        return dataSpanMs >= button.count * oneMonthMs;
      if (button.type === "year") return dataSpanMs >= button.count * oneYearMs;
      return true;
    });
  }
}
